/**
 * Certificate — Premium academic redesign (Feb 2026).
 *
 * Layout:
 *   - Single centered column on a near-white cream background
 *   - Thin gold accent borders (no heavy frames, no left sidebar)
 *   - Serif title + serif student name (Playfair Display via Google Fonts CDN)
 *   - Clean sans-serif (Inter, system fallback) for body copy
 *   - Subtle embossed circular seal — "Certified Youth Entrepreneur"
 *   - Optional QR code linking to /verify/{cert_id}
 *   - Footer: founder + LemonBot signatures + small WE Mentality logo
 *
 * Print + html2canvas-safe — only same-origin assets and CSS.
 */
import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import axios from "axios";
import QRCode from "qrcode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WE_MENTALITY_LOGO = "/cert-assets/we-mentality.png";

// Areas of proficiency
const PROFICIENCIES = [
  "Idea Generation and Problem Solving",
  "Teamwork and Communication",
  "Business Planning and Organization",
  "Financial Literacy and Pricing Strategy",
  "Marketing and Customer Experience",
  "Confidence, Reflection, and Growth",
];

// Embedded inline SVG seal — keeps html2canvas happy and renders crisply.
const SealSVG = () => (
  <svg
    viewBox="0 0 160 160"
    className="cert-seal"
    role="img"
    aria-label="Certified Youth Entrepreneur seal"
  >
    <defs>
      <radialGradient id="goldFill" cx="38%" cy="32%" r="65%">
        <stop offset="0%" stopColor="#FFF6CC" />
        <stop offset="55%" stopColor="#E0B844" />
        <stop offset="100%" stopColor="#9C7A1F" />
      </radialGradient>
      <linearGradient id="goldRim" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#FFE08A" />
        <stop offset="100%" stopColor="#A88433" />
      </linearGradient>
    </defs>
    {/* outer ring */}
    <circle cx="80" cy="80" r="76" fill="url(#goldRim)" />
    <circle cx="80" cy="80" r="69" fill="url(#goldFill)" />
    {/* inner double ring */}
    <circle cx="80" cy="80" r="62" fill="none" stroke="#7A5A14" strokeWidth="0.6" opacity="0.55" />
    <circle cx="80" cy="80" r="58" fill="none" stroke="#7A5A14" strokeWidth="0.4" opacity="0.4" />
    {/* curved top text via path */}
    <defs>
      <path id="topArc" d="M 26 80 A 54 54 0 0 1 134 80" fill="none" />
      <path id="botArc" d="M 30 84 A 50 50 0 0 0 130 84" fill="none" />
    </defs>
    <text fill="#5C4108" fontSize="9.5" fontFamily="Georgia, serif" letterSpacing="2.4" fontWeight="700">
      <textPath xlinkHref="#topArc" startOffset="50%" textAnchor="middle">
        CERTIFIED YOUTH
      </textPath>
    </text>
    <text fill="#5C4108" fontSize="9.5" fontFamily="Georgia, serif" letterSpacing="2.4" fontWeight="700">
      <textPath xlinkHref="#botArc" startOffset="50%" textAnchor="middle">
        ENTREPRENEUR
      </textPath>
    </text>
    {/* center monogram */}
    <text x="80" y="92" fontSize="40" fontFamily="Georgia, serif" fontWeight="700" textAnchor="middle" fill="#3F2C04">
      WE
    </text>
    {/* tiny stars flanking */}
    <text x="34" y="86" fontSize="11" fill="#5C4108">★</text>
    <text x="120" y="86" fontSize="11" fill="#5C4108">★</text>
  </svg>
);


function Certificate({ user }) {
  const certificateRef = useRef(null);
  const [certId, setCertId] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);

  // Load fonts once (Playfair Display + Inter via Google Fonts).
  useEffect(() => {
    const linkId = "cert-fonts";
    if (document.getElementById(linkId)) return;
    const link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=Inter:wght@400;500;600&display=swap";
    document.head.appendChild(link);
  }, []);

  // Mint or fetch the certificate ID.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/certificate/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!cancelled) setCertId(res.data.certificate_id);
      } catch (e) {
        // Non-fatal — certificate still renders without an ID
        console.warn("Could not fetch certificate id:", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Render QR pointing at the public verification page once we have an ID.
  useEffect(() => {
    if (!certId) return;
    const url = `${window.location.origin}/verify/${certId}`;
    QRCode.toDataURL(url, {
      margin: 1,
      width: 240,
      color: { dark: "#1F2937", light: "#FFFFFF" },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [certId]);

  const handleDownload = async () => {
    if (!certificateRef.current) return;
    try {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        backgroundColor: "#FFFDF7",
        useCORS: true,
        allowTaint: true,
      });
      const link = document.createElement("a");
      link.download = `${(user.name || "Student").replace(/\s+/g, "_")}_Certificate.png`;
      link.href = canvas.toDataURL("image/png");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating certificate:", error);
      alert("Failed to download certificate. Please try again.");
    }
  };

  const handlePrint = () => window.print();

  const formattedDate = new Date().toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="certificate-container">
      <div
        ref={certificateRef}
        className="certificate-premium"
        data-testid="certificate-premium"
      >
        {/* === BRAND LINE === */}
        <p className="cert-brand" data-testid="certificate-brand-line">
          The WE Mentality &nbsp;|&nbsp; Lemonade Stand Business Plan
        </p>

        {/* === GOLD ACCENT === */}
        <div className="cert-accent" aria-hidden="true">
          <span className="cert-accent-dot" />
          <span className="cert-accent-line" />
          <span className="cert-accent-dot" />
        </div>

        {/* === TITLE === */}
        <h1 className="cert-title">Certificate of Completion</h1>

        {/* === RECOGNITION === */}
        <p className="cert-recognition">
          This certifies that
        </p>

        {/* === STUDENT NAME === */}
        <h2 className="cert-name" data-testid="certificate-student-name">
          {user?.name || "Student"}
        </h2>

        <p className="cert-body">
          has successfully completed the <b>Lemonade Stand Business Plan Youth Entrepreneurship Program</b>
          {" "}and demonstrated proficiency in the following areas:
        </p>

        {/* === PROFICIENCY GRID === */}
        <ul className="cert-proficiencies">
          {PROFICIENCIES.map((p, i) => (
            <li key={i}>
              <span className="cert-bullet" aria-hidden="true">◆</span>
              {p}
            </li>
          ))}
        </ul>

        {/* === SEAL === */}
        <div className="cert-seal-wrap" data-testid="certificate-seal">
          <SealSVG />
        </div>

        {/* === PROGRAM INFO ROW === */}
        <div className="cert-program-row">
          <div className="cert-program-col" style={{ textAlign: "left" }}>
            <p className="cert-program-label">Program</p>
            <p className="cert-program-value">Lemonade Stand Business Plan · Youth Bootcamp</p>
          </div>
          <div className="cert-program-col" style={{ textAlign: "right" }}>
            <p className="cert-program-label">Completion Date</p>
            <p className="cert-program-value" data-testid="certificate-date">{formattedDate}</p>
          </div>
        </div>

        {/* === SIGNATURES === */}
        <div className="cert-signatures">
          <div className="cert-sig">
            <span className="cert-sig-name">Brandon Ward</span>
            <span className="cert-sig-line" />
            <span className="cert-sig-role">Founder, The WE Mentality</span>
          </div>
          <div className="cert-sig">
            <span className="cert-sig-name">LemonBot</span>
            <span className="cert-sig-line" />
            <span className="cert-sig-role">AI Business Coach</span>
          </div>
        </div>

        {/* === FOOTER (logo + cert id + qr) === */}
        <div className="cert-footer">
          <div className="cert-footer-left">
            <img
              src={WE_MENTALITY_LOGO}
              alt="The WE Mentality"
              className="cert-footer-logo"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
          <div className="cert-footer-center">
            {certId && (
              <p className="cert-id" data-testid="certificate-id">
                Certificate No. <b>{certId}</b>
              </p>
            )}
            {certId && (
              <p className="cert-verify-hint">
                Verify at <span>{(BACKEND_URL || "").replace(/^https?:\/\//, "")}/verify/{certId}</span>
              </p>
            )}
          </div>
          <div className="cert-footer-right">
            {qrDataUrl && (
              <img
                src={qrDataUrl}
                alt="Verification QR code"
                className="cert-qr"
                data-testid="certificate-qr"
              />
            )}
          </div>
        </div>
      </div>

      {/* === ACTION BUTTONS === */}
      <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8 certificate-actions print:hidden" role="group" aria-label="Certificate actions">
        <button
          onClick={handleDownload}
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all flex items-center justify-center gap-2 focus:ring-4 focus:ring-blue-300"
          aria-label="Download your certificate as an image"
          data-testid="download-certificate-btn"
        >
          <span aria-hidden="true">📥</span> Download Certificate
        </button>
        <button
          onClick={handlePrint}
          className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all flex items-center justify-center gap-2 focus:ring-4 focus:ring-green-300"
          aria-label="Print your certificate"
          data-testid="print-certificate-btn"
        >
          <span aria-hidden="true">🖨️</span> Print Certificate
        </button>
      </div>

      {/* === STYLES (scoped) === */}
      <style>{`
        .certificate-premium {
          position: relative;
          max-width: 1024px;
          margin: 0 auto;
          background: #FFFDF7;
          color: #1F2937;
          padding: 56px 72px 44px;
          border: 1px solid #E5E7EB;
          border-top: 6px solid #C9A437;
          border-bottom: 6px solid #C9A437;
          box-shadow: 0 12px 36px rgba(0, 0, 0, 0.08);
          font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          text-align: center;
        }
        .cert-brand {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: #92722B;
          margin: 0 0 18px 0;
        }
        .cert-accent {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin: 0 auto 14px;
          width: 60%;
        }
        .cert-accent-dot {
          width: 6px;
          height: 6px;
          border-radius: 999px;
          background: #C9A437;
        }
        .cert-accent-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, rgba(201,164,55,0) 0%, #C9A437 50%, rgba(201,164,55,0) 100%);
        }
        .cert-title {
          font-family: "Playfair Display", Georgia, "Times New Roman", serif;
          font-weight: 700;
          font-size: 44px;
          line-height: 1.15;
          color: #111827;
          margin: 4px 0 22px 0;
          letter-spacing: 0.5px;
        }
        .cert-recognition {
          font-size: 14px;
          color: #6B7280;
          margin: 0 0 6px 0;
          font-style: italic;
        }
        .cert-name {
          font-family: "Playfair Display", Georgia, "Times New Roman", serif;
          font-weight: 700;
          font-style: italic;
          font-size: 56px;
          line-height: 1.1;
          color: #1F2937;
          margin: 4px 0 14px 0;
          letter-spacing: 0.3px;
        }
        .cert-body {
          font-size: 14px;
          line-height: 1.6;
          color: #374151;
          max-width: 720px;
          margin: 0 auto 18px auto;
        }
        .cert-proficiencies {
          list-style: none;
          padding: 0;
          margin: 6px auto 24px;
          max-width: 720px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px 28px;
          font-size: 13px;
          color: #1F2937;
          text-align: left;
        }
        .cert-proficiencies li {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          line-height: 1.4;
        }
        .cert-bullet {
          color: #C9A437;
          font-size: 9px;
          margin-top: 5px;
          flex-shrink: 0;
        }
        .cert-seal-wrap {
          margin: 8px auto 18px;
          width: 110px;
          height: 110px;
          filter: drop-shadow(0 4px 10px rgba(122, 90, 20, 0.18));
        }
        .cert-seal {
          width: 100%;
          height: 100%;
        }
        .cert-program-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 28px;
          padding: 14px 0 6px;
          margin: 0 0 28px 0;
          border-top: 1px solid #EAD9A8;
          border-bottom: 1px solid #EAD9A8;
        }
        .cert-program-col {
          flex: 1;
          padding: 10px 0;
        }
        .cert-program-label {
          font-size: 10px;
          letter-spacing: 1.6px;
          text-transform: uppercase;
          color: #92722B;
          font-weight: 600;
          margin: 0 0 3px 0;
        }
        .cert-program-value {
          font-size: 14px;
          font-weight: 600;
          color: #1F2937;
          margin: 0;
        }
        .cert-signatures {
          display: flex;
          justify-content: space-around;
          gap: 60px;
          margin: 8px 0 28px 0;
        }
        .cert-sig {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
          max-width: 280px;
        }
        .cert-sig-name {
          font-family: "Playfair Display", Georgia, serif;
          font-style: italic;
          font-size: 22px;
          color: #1F2937;
          margin-bottom: 4px;
        }
        .cert-sig-line {
          width: 100%;
          height: 1px;
          background: #9CA3AF;
          margin: 4px 0;
        }
        .cert-sig-role {
          font-size: 11px;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: #6B7280;
          font-weight: 500;
        }
        .cert-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding-top: 18px;
          border-top: 1px solid #F3E8C2;
        }
        .cert-footer-left, .cert-footer-right {
          flex: 0 0 110px;
          display: flex;
          align-items: center;
        }
        .cert-footer-right { justify-content: flex-end; }
        .cert-footer-center {
          flex: 1;
          text-align: center;
        }
        .cert-footer-logo {
          width: 80px;
          height: auto;
          object-fit: contain;
          opacity: 0.92;
        }
        .cert-id {
          font-size: 11px;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #6B7280;
          margin: 0;
          font-weight: 500;
        }
        .cert-id b {
          color: #1F2937;
          font-weight: 700;
          letter-spacing: 1px;
        }
        .cert-verify-hint {
          font-size: 10px;
          color: #9CA3AF;
          margin: 4px 0 0 0;
          font-style: italic;
        }
        .cert-verify-hint span {
          color: #6B7280;
        }
        .cert-qr {
          width: 84px;
          height: 84px;
          border: 1px solid #E5E7EB;
          padding: 4px;
          background: white;
        }

        @media (max-width: 720px) {
          .certificate-premium { padding: 36px 24px 28px; }
          .cert-title { font-size: 30px; }
          .cert-name { font-size: 36px; }
          .cert-proficiencies { grid-template-columns: 1fr; }
          .cert-signatures { flex-direction: column; gap: 24px; }
          .cert-footer { flex-direction: column; gap: 12px; }
          .cert-footer-left, .cert-footer-right { flex: none; }
        }
        @media print {
          body * { visibility: hidden; }
          .certificate-container, .certificate-container * { visibility: visible; }
          .certificate-container { position: absolute; left: 0; top: 0; width: 100%; }
          .certificate-actions { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default Certificate;
